import type { User } from '@auth0/auth0-react'

import { parseJwt } from '@util/auth-util'

import type { AuthenticatedUser } from './types/AuthenticatedUser'

export const createAuthenticatedUser = (
  user?: User & { accessToken?: string },
): AuthenticatedUser => {
  let grantedScopes: string[] = []

  if (user?.accessToken) {
    grantedScopes = parseJwt(user.accessToken)['permissions']
  }

  const hasScopes = (scopes: string[]) =>
    scopes.every((scope) => grantedScopes.includes(scope))

  const combi = user && user['http://capo.dsplatform.nl/combi']
  const deelnemer = user && user['http://analytics.dsplatform.nl/deelnemer']
  const delegerende = user && user['http://analytics.dsplatform.nl/delegerende']

  const organisatie = (combi || delegerende || deelnemer) as string

  return {
    combi,
    deelnemer,
    delegerende,
    organisatie,
    hasPermissions: !!user?.accessToken,
    // View
    canViewDashboard: hasScopes(['show:dashboard']),
    canViewProjecten: hasScopes(['show:aanvragen']),
    canViewOpdrachten: hasScopes(['show:deelnemer_opdrachten']),
    canViewCombiOpdrachten: hasScopes(['show:opdrachten']),
    canViewActiviteiten: hasScopes(['show:activiteiten']),
    canViewAssignedBijlagen: hasScopes(['show:bijlage_assigned']),
    canViewProjectmeldingen: hasScopes(['show:projectmeldingen']),
    canViewProjectdossiers: hasScopes(['show:projectdossiers']),
    canViewContracten: hasScopes(['show:contracten']),
    canViewPlanning: hasScopes(['show:planning']),
    canViewBalkenplanning: hasScopes(['show:balkenplanning']),
    canViewKlantgegevens: hasScopes(['show:klant']),
    canViewKlantVraagAntwoord: hasScopes(['show:klant_vragen']),
    canViewQuickScan: hasScopes(['show:quickscan_melden']),
    canViewCombiUitvoeringMelden: hasScopes(['show:combiuitvoering_melden']),
    canViewActiefMelden: hasScopes(['show:actief_melden']),
    canViewUpdateLabels: hasScopes(['show:update_labels']),
    canViewHoofdnetOpdrachten: hasScopes(['show:hoofdnet_opdrachten']),
    canViewPlanningsverschil: hasScopes(['show:planningsverschil']),
    // Create
    canCreateActiviteit: hasScopes(['create:activiteiten']),
    canCreateHerplanning: hasScopes(['create:event_herplanning']),
    canCreateHerplanningKlant: hasScopes(['create:event_herplanning_klant']),
    canCreateAssignedBijlage: hasScopes(['show:create_assigned_bijlage']),
    canCreateProjectdossier: hasScopes(['create:projectdossier']),
    canCreateProject: hasScopes(['create:project']),
    canChangeProject: hasScopes(['create:event_hoofdleiding_details']),
    canCreateWijzigenDisciplines: hasScopes([
      'create:event_hoofdleiding_disciplines',
    ]),
    canCreateWijzigenAannemer: hasScopes([
      'create:event_hoofdleiding_aannemer',
    ]),
    canCreateProcesstap: hasScopes(['create:event_hoofdleiding_processtap']),
    canCreateVerantwoordelijke: hasScopes([
      'create:event_hoofdleiding_verantwoordelijke',
    ]),
    canCreateHoofdleidingGebiedswissel: hasScopes([
      'create:event_hoofdleiding_gebiedswissel',
    ]),
    switchUserParam: hasScopes(['switch:user_param']),
    canViewBeheer: hasScopes(['show:beheer']),
    canViewGebiedswissel: hasScopes(['show:gebiedswissel']),
    canViewAanvraagImporteren: hasScopes(['show:import_aanvraag']),
    canViewAanvraagKopieren: hasScopes(['copy:aanvraag']),
    canCreateGebiedswissel: hasScopes(['create:event_gebiedswissel']),
    canKoppelProject: hasScopes(['create:event_project_koppelen']),
    canCreateReferentie: hasScopes(['create:event_hoofdleiding_referentie']),
    canCreateGeblokkeerdeWeek: hasScopes(['create:geblokkeerde_week']),
    canCreateTechnischGereed: hasScopes(['create:event_technisch_gereed']),
    canCreateSubprocesSchouw: hasScopes(['create:subproces_schouw']),
    canCreateSubprocesBodem: hasScopes(['create:subproces_bodem']),
    canCreateSubprocesNetcheck: hasScopes(['create:subproces_netcheck']),
    canCreateJobSchedule: hasScopes(['create:jobschedule']),
    canCreateProjectGroup: hasScopes(['create:event_update_project_group']),
    canCreateProjectbetrokkene: hasScopes(['create:projectbetrokkene']),
    canCreateLabel: hasScopes(['create:label']),
    canCreateTracelengte: hasScopes(['create:event_hoofdleiding_tracelengte']),
    canCreateWijzigenGeschovenNaarAannemer: hasScopes([
      'create:event_wijzigen_geschoven_naar_aannemer',
    ]),

    // update
    canUpdateBijlageTemplate: hasScopes(['update:bijlage_template']),
    canUpdateProjectdossier: hasScopes(['update:projectdossier']),
  }
}
